import { z } from 'zod'
import { publicIdSchema, utcTimestampSchema } from './primitives'

export const CATEGORY_STATUS_VALUES = ['active', 'archived'] as const
export const categoryStatusSchema = z.enum(CATEGORY_STATUS_VALUES)
export type CategoryStatus = z.infer<typeof categoryStatusSchema>

export const competitionCategorySchema = z.object({
  id: publicIdSchema,
  competitionId: publicIdSchema,
  name: z.string().trim().min(1).max(120),
  description: z.string().max(2000).nullable(),
  /** Maximum number of teams; null means unlimited. */
  capacity: z.int().min(1).max(100_000).nullable(),
  sortOrder: z.int().min(0).max(10_000),
  status: categoryStatusSchema,
  createdAt: utcTimestampSchema,
  updatedAt: utcTimestampSchema,
})

export type CompetitionCategory = z.infer<typeof competitionCategorySchema>

export const competitionCategoryDtoSchema = competitionCategorySchema.omit({
  competitionId: true,
  updatedAt: true,
})

export type CompetitionCategoryDto = z.infer<typeof competitionCategoryDtoSchema>

export const createCompetitionCategoryRequestSchema = z.strictObject({
  name: z.string().trim().min(1).max(120),
  description: z.string().max(2000).nullable().default(null),
  capacity: z.int().min(1).max(100_000).nullable().default(null),
  sortOrder: z.int().min(0).max(10_000).default(0),
})

export type CreateCompetitionCategoryRequest = z.infer<
  typeof createCompetitionCategoryRequestSchema
>

/**
 * Every field optional, but at least one required — a PATCH with an empty body
 * would otherwise write an audit entry recording that nothing changed.
 */
export const updateCompetitionCategoryRequestSchema = z
  .strictObject({
    name: z.string().trim().min(1).max(120).optional(),
    description: z.string().max(2000).nullable().optional(),
    capacity: z.int().min(1).max(100_000).nullable().optional(),
    sortOrder: z.int().min(0).max(10_000).optional(),
    status: categoryStatusSchema.optional(),
  })
  .refine((value) => Object.keys(value).length > 0, {
    message: 'At least one field must be provided.',
  })

export type UpdateCompetitionCategoryRequest = z.infer<
  typeof updateCompetitionCategoryRequestSchema
>
